import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, MapPin, Globe, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else if (searchQuery.length === 0) {
        setResults([]);
        setHasSearched(false);
      }
    }, 500); // Debounce search

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const performSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: searchQuery },
        withCredentials: true
      });
      setResults(response.data);
    } catch (e) {
      console.error('Search failed:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/discover')}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
            
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or location..."
                className="w-full pl-12 pr-4 py-6 rounded-full border-2 border-purple-200 focus:border-purple-400 text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Search Info */}
        {!hasSearched && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-purple-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Search Users</h2>
            <p className="text-gray-500 mb-2">Find matches by name or where they're located</p>
            <p className="text-sm text-gray-400">Try searching "Tokyo", "New York", or a name</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent mx-auto"></div>
            <p className="text-gray-500 mt-4">Searching...</p>
          </div>
        )}

        {/* Results */}
        {!loading && hasSearched && results.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found matching "{searchQuery}"</p>
            <p className="text-sm text-gray-400 mt-2">Try a different search term</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">Found {results.length} user{results.length !== 1 ? 's' : ''}</p>
            
            {results.map((user) => (
              <div
                key={user.id}
                data-testid={`search-result-${user.id}`}
                onClick={() => navigate(`/profile/${user.id}`)}
                className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer"
                style={{transition: 'all 0.3s ease'}}
              >
                <div className="flex items-center space-x-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={user.photos?.[0]?.startsWith('http') ? user.photos[0] : (user.photos?.[0] ? `${BACKEND_URL}${user.photos[0]}` : user.picture)} />
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white text-xl">
                      {user.name[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-800 truncate">{user.name}</h3>
                      {user.verification_status === 'approved' && (
                        <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          <CheckCircle className="w-3 h-3 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">Verified</span>
                        </div>
                      )}
                      <span className="text-gray-600">{user.age}</span>
                    </div>
                    
                    {user.location && (
                      <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{user.location}</span>
                      </div>
                    )}
                    
                    {user.bio && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">{user.bio}</p>
                    )}
                    
                    {user.countries_to_visit?.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {user.countries_to_visit.slice(0, 3).map((country, idx) => (
                            <Badge key={idx} className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                              {country}
                            </Badge>
                          ))}
                          {user.countries_to_visit.length > 3 && (
                            <span className="text-xs text-gray-500">+{user.countries_to_visit.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
